import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import styles from "./styles.module.scss";
import { ReactComponent as Logo } from "../../../assets/James-Hope-University-Logo.svg";
import { useAtomValue } from "jotai";
import { userAtom } from "../../../utils/store";
import { useApplicantData } from "../../../hooks/useApplicantData";
import { useApplicationPeriod } from "../../../hooks/useApplicationPeriod";
import { Spin } from "antd";
import { formatFullDate } from "../../../utils/formatDate";
import { isValidDate } from "../../../utils";
import { formatCurrency } from "../../../utils/currency";
const documentName = (str?: string | null) =>
  str ? str.slice(str.lastIndexOf("/") + 1) : "No file uploaded";

const RegistrationForm = () => {
  const user = useAtomValue(userAtom);
  const printRef = useRef<HTMLDivElement>(null);
  const {
    personalInfo,
    institutions,
    workHistory,
    nyscUpload,
    qualifications,
    isLoading,
  } = useApplicantData();
  const { shouldPayLateApplicationFee, lateApplicationFee } =
    useApplicationPeriod();

  const printDocument = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Application_Form",
    pageStyle: `
      @page {
        size: A4;
        margin: 15mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        * {
          overflow: visible !important;
          height: auto !important;
        }
      }
    `,
  });

  if (isLoading) {
    return <Spin size="large" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <h2>Application Form Preview</h2>
        <button onClick={printDocument} className={styles.printButton}>
          Print Application Form
        </button>
      </div>

      <div className={styles.formWrapper} ref={printRef}>
        {/* Header Section */}
        <div className={styles.formHeader}>
          <div className={styles.headerContent}>
            <div className={styles.logoSection}>
              <Logo className={styles.logo} />
            </div>
            <div className={styles.headerText}>
              <h1>JAMES HOPE UNIVERSITY</h1>
              <h2>APPLICATION FORM</h2>
              <p className={styles.sessionInfo}>
                Academic Session: {user?.sessionId}
              </p>
            </div>
          </div>
          <div className={styles.applicationNumber}>
            Application No: <strong>{personalInfo?.applicationNumber}</strong>
          </div>
        </div>

        {/* Program Information */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>PROGRAMME INFORMATION</h3>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.grid}>
              <div className={styles.fieldGroup}>
                <label>Programme:</label>
                <span>{user?.programName}</span>
              </div>
              <div className={styles.fieldGroup}>
                <label>Degree Type:</label>
                <span>{user?.programType}</span>
              </div>
              <div className={styles.fieldGroup}>
                <label>Mode of Study:</label>
                <span>{user?.modeOfStudyName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>SECTION 1: PERSONAL INFORMATION</h3>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.personalInfoLayout}>
              <div className={styles.photoSection}>
                <div className={styles.passportPhoto}>
                  <img
                    src={personalInfo?.imageUrl}
                    alt="Applicant"
                    className={styles.photo}
                  />
                </div>
                <p className={styles.photoLabel}>Passport Photograph</p>
              </div>
              <div className={styles.personalDetails}>
                <div className={styles.gridThree}>
                  <div className={styles.fieldGroup}>
                    <label>First Name:</label>
                    <span>{personalInfo?.fName}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Middle Name:</label>
                    <span>{personalInfo?.mNane}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Last Name:</label>
                    <span>{personalInfo?.lName}</span>
                  </div>
                </div>
                <div className={styles.gridTwo}>
                  <div className={styles.fieldGroup}>
                    <label>Gender:</label>
                    <span>{personalInfo?.gender}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Date of Birth:</label>
                    <span>{formatFullDate(personalInfo?.dateOfBirth)}</span>
                  </div>
                </div>
                <div className={styles.gridTwo}>
                  <div className={styles.fieldGroup}>
                    <label>Phone Number:</label>
                    <span>{personalInfo?.phoneNo}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Email Address:</label>
                    <span>{personalInfo?.email}</span>
                  </div>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Residential Address:</label>
                  <span>{personalInfo?.address}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Country:</label>
                  <span>{personalInfo?.countryName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Academic Qualifications */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>SECTION 2: ACADEMIC QUALIFICATIONS</h3>
          </div>
          <div className={styles.sectionContent}>
            <h4 className={styles.subsectionTitle}>
              A. Post-Secondary Education
            </h4>
            {institutions?.map((institution, index) => (
              <div key={institution.id} className={styles.institutionBlock}>
                <div className={styles.gridTwo}>
                  <div className={styles.fieldGroup}>
                    <label>Institution:</label>
                    <span>{institution.institutionName}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Discipline/Major:</label>
                    <span>{institution.discipline}</span>
                  </div>
                </div>
                <div className={styles.gridThree}>
                  <div className={styles.fieldGroup}>
                    <label>Start Date:</label>
                    <span>{formatFullDate(institution.startDate)}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>End Date:</label>
                    <span>{formatFullDate(institution.endDate)}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>CGPA:</label>
                    <span>{institution.cgpa}/5.0</span>
                  </div>
                </div>
                {index < institutions?.length - 1 && (
                  <hr className={styles.divider} />
                )}
              </div>
            ))}

            {qualifications && qualifications.length > 0 && (
              <h4 className={styles.subsectionTitle}>
                B. O'Level Qualifications
              </h4>
            )}
            {qualifications?.map((qualification, index) => (
              <div key={qualification.id} className={styles.qualificationBlock}>
                <div className={styles.gridTwo}>
                  <div className={styles.fieldGroup}>
                    <label>Qualification Type:</label>
                    <span>{qualification.qualificationType}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Examination Number:</label>
                    <span>{qualification.examNumber}</span>
                  </div>
                </div>
                <div className={styles.resultTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Subject</th>
                        <th>Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qualification.subjects.map((subject, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{subject.subject}</td>
                          <td>{subject.grade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {index < qualifications?.length - 1 && (
                  <hr className={styles.divider} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Work Experience */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>SECTION 3: WORK EXPERIENCE</h3>
          </div>
          <div className={styles.sectionContent}>
            {workHistory?.map((work, index) => (
              <div key={work.id} className={styles.workBlock}>
                <div className={styles.gridTwo}>
                  <div className={styles.fieldGroup}>
                    <label>Company/Organization:</label>
                    <span>{work.companyName}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Position/Role:</label>
                    <span>{work.position}</span>
                  </div>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Duration:</label>
                  <span>
                    {formatFullDate(work.startDate)} -{" "}
                    {isValidDate(work.endDate)
                      ? formatFullDate(work.endDate)
                      : "Present"}
                  </span>
                </div>
                {index < workHistory?.length - 1 && (
                  <hr className={styles.divider} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Documents Uploaded */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>SECTION 4: DOCUMENTS UPLOADED</h3>
          </div>
          <div className={styles.sectionContent}>
            <ul className={styles.docList}>
              <li>
                <strong>NYSC Document:</strong>{" "}
                <a
                  href={nyscUpload?.nyscUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.certificateLink}
                >
                  {documentName(nyscUpload?.nyscUrl)}
                </a>
              </li>

              {qualifications?.map((q) => (
                <li key={q.id}>
                  <strong>{q.qualificationType} Certificate:</strong>{" "}
                  <a
                    href={(q as any)?.certificateiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.certificateLink}
                  >
                    {documentName((q as any)?.certificateiUrl)}
                  </a>
                </li>
              ))}

              {workHistory?.map((w) => (
                <li key={w.id}>
                  <strong>Employment Document ({w.companyName}):</strong>{" "}
                  <a
                    href={(w as any)?.employmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.certificateLink}
                  >
                    {documentName((w as any)?.employmentUrl)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Summary */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>SECTION 5: PAYMENT SUMMARY</h3>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.paymentTable}>
              <table>
                <tbody>
                  <tr>
                    <td>Application Fee:</td>
                    <td className={styles.amount}>
                      {formatCurrency(user?.earlyApplicationAmount ?? 0)}
                    </td>
                  </tr>
                  {shouldPayLateApplicationFee && (
                    <tr>
                      <td>Late Application Fee:</td>
                      <td className={styles.amount}>
                        {formatCurrency(lateApplicationFee)}
                      </td>
                    </tr>
                  )}
                  {/* <tr>
                    <td>Discount:</td>
                    <td className={styles.amount}>-{discount}</td>
                  </tr>
                  <tr className={styles.totalRow}>
                    <td>
                      <strong>Total Amount:</strong>
                    </td>
                    <td className={styles.amount}>
                      <strong>{totalAmount}</strong>
                    </td>
                  </tr> */}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Declaration */}
        {/* <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>DECLARATION</h3>
          </div>
          <div className={styles.sectionContent}>
            <p className={styles.declaration}>
              I hereby declare that all information provided in this application
              form is true and accurate to the best of my knowledge. I
              understand that any false information may lead to the cancellation
              of my admission.
            </p>
            <div className={styles.signatureSection}>
              <div className={styles.signatureBlock}>
                <div className={styles.signatureLine}></div>
                <label>Applicant's Signature</label>
              </div>
              <div className={styles.signatureBlock}>
                <div className={styles.signatureLine}></div>
                <label>Date</label>
              </div>
            </div>
          </div>
        </div> */}

        {/* Footer */}
        {/* <div className={styles.formFooter}>
          <p>
            For official use only | Processed by: __________ | Date: __________
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default RegistrationForm;
